import { useCallback } from "react";
import { createSession, streamMessage, resumeMessage as resumeMessageApi } from "@/lib/api";

/**
 * Encapsulates the SSE send + resume loops for the chat page.
 *
 * Owns NO state of its own — it mutates messages/sending via the setters
 * passed in. This keeps Chat.jsx's orchestration logic intact while
 * pulling the 230-line streaming reducer out of the component body.
 *
 * The original Chat.jsx implementation is preserved verbatim in spirit:
 *   - optimistic user-message
 *   - placeholder streaming-assistant bubble
 *   - assistant_start binds server id onto the placeholder
 *   - chunks append delta to that bubble
 *   - assistant_complete replaces with the final doc
 *   - error stops streaming, leaves any partial content visible
 *   - one auto-resume after a mid-flight error (if there was content)
 */
export function useChatStream({
  agentId,
  activeSessionId,
  activeWorkspaceId,
  setActiveSessionId,
  setMessages,
  setSending,
  loadSessions,
  skipNextLoadRef,
}) {
  // ---------- Resume (used both manually and as auto-recovery) ----------
  const resume = useCallback(
    async (messageId, autoTriggered = false) => {
      // The send path always sets sending=true before calling us when
      // auto-triggered, so we must not bail in that case.
      setSending(true);
      try {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? { ...m, truncated: false, streaming: true, autoResuming: autoTriggered }
              : m
          )
        );
        let finalDoc = null;
        let chunkCount = 0;
        let errorMsg = null;
        for await (const evt of resumeMessageApi(activeSessionId, messageId)) {
          if (evt.event === "chunk") {
            chunkCount += 1;
            const delta = evt.data?.delta || "";
            setMessages((prev) =>
              prev.map((m) =>
                m.id === messageId ? { ...m, content: m.content + delta } : m
              )
            );
          } else if (evt.event === "assistant_complete") {
            finalDoc = evt.data;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === messageId
                  ? { ...finalDoc, streaming: false, autoResuming: false }
                  : m
              )
            );
          } else if (evt.event === "error") {
            errorMsg = evt.data?.message || "Resume error";
          }
        }
        if (errorMsg) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === messageId
                ? {
                    ...m,
                    streaming: false,
                    autoResuming: false,
                    truncated: true,
                    content: m.content + `\n\n⚠️ ${errorMsg}`,
                  }
                : m
            )
          );
        } else if (!finalDoc && chunkCount === 0) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === messageId
                ? {
                    ...m,
                    streaming: false,
                    autoResuming: false,
                    truncated: true,
                    content:
                      m.content +
                      "\n\n⚠️ Resume produced no content — please try again.",
                  }
                : m
            )
          );
        } else if (!finalDoc) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === messageId
                ? { ...m, streaming: false, autoResuming: false }
                : m
            )
          );
        }
        await loadSessions();
      } catch (e) {
        console.error(e);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  streaming: false,
                  autoResuming: false,
                  truncated: true,
                  content:
                    m.content + `\n\n⚠️ ${e?.message || "Resume failed"}`,
                }
              : m
          )
        );
      } finally {
        setSending(false);
      }
    },
    [activeSessionId, loadSessions, setMessages, setSending]
  );

  // ---------- Send ----------
  const send = useCallback(
    async (content, attachments, autoResumeOnTruncate = true) => {
      setSending(true);
      try {
        let sessId = activeSessionId;
        if (!sessId) {
          const sess = await createSession(
            agentId,
            undefined,
            activeWorkspaceId || undefined
          );
          sessId = sess.id;
          skipNextLoadRef.current = true;
          setActiveSessionId(sessId);
        }

        const optimisticUser = {
          id: `tmp-user-${Date.now()}`,
          role: "user",
          content,
          attachments,
          created_at: new Date().toISOString(),
        };
        const streamingId = `tmp-assistant-${Date.now()}`;
        const streamingAssistant = {
          id: streamingId,
          role: "assistant",
          content: "",
          attachments: [],
          created_at: new Date().toISOString(),
          streaming: true,
        };
        setMessages((prev) => [...prev, optimisticUser, streamingAssistant]);

        let realAssistant = null;
        let realAssistantId = null;
        let chunkCount = 0;
        let streamErrored = false;

        for await (const evt of streamMessage(sessId, content, attachments)) {
          if (evt.event === "user_message") {
            const realUser = evt.data;
            setMessages((prev) =>
              prev.map((m) => (m.id === optimisticUser.id ? realUser : m))
            );
          } else if (evt.event === "assistant_start") {
            realAssistantId = evt.data?.id;
            const objectContext = evt.data?.object_context || null;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === streamingId ? { ...m, id: realAssistantId, object_context: objectContext } : m
              )
            );
          } else if (evt.event === "chunk") {
            chunkCount += 1;
            const delta = evt.data?.delta || "";
            setMessages((prev) =>
              prev.map((m) =>
                m.id === (realAssistantId || streamingId)
                  ? { ...m, content: m.content + delta }
                  : m
              )
            );
          } else if (evt.event === "assistant_complete") {
            realAssistant = evt.data;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === (realAssistantId || streamingId)
                  ? { ...realAssistant, streaming: false }
                  : m
              )
            );
          } else if (evt.event === "error") {
            streamErrored = true;
            const errMsg = evt.data?.message || "Stream error";
            setMessages((prev) =>
              prev.map((m) =>
                m.id === (realAssistantId || streamingId)
                  ? {
                      ...m,
                      content: chunkCount > 0 ? m.content : `⚠️ ${errMsg}`,
                      streaming: false,
                      truncated: chunkCount > 0,
                    }
                  : m
              )
            );
          }
        }

        if (!realAssistant) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === (realAssistantId || streamingId)
                ? {
                    ...m,
                    streaming: false,
                    content: m.content || "⚠️ Empty response",
                  }
                : m
            )
          );
        }
        await loadSessions();

        // One-shot auto-resume after a mid-flight error.
        if (
          autoResumeOnTruncate &&
          streamErrored &&
          chunkCount > 0 &&
          realAssistantId
        ) {
          await new Promise((r) => setTimeout(r, 1800));
          // resume() will toggle sending false/true around itself.
          await resume(realAssistantId, /* autoTriggered */ true);
          return;
        }
      } catch (err) {
        console.error(err);
        const msg =
          err?.message || "The agent could not respond. Please try again.";
        setMessages((prev) => [
          ...prev.filter((m) => !String(m.id).startsWith("tmp-")),
          {
            id: `err-${Date.now()}`,
            role: "assistant",
            content: `⚠️ ${msg}`,
            created_at: new Date().toISOString(),
          },
        ]);
      } finally {
        setSending(false);
      }
    },
    [
      agentId,
      activeSessionId,
      activeWorkspaceId,
      loadSessions,
      resume,
      setActiveSessionId,
      setMessages,
      setSending,
      skipNextLoadRef,
    ]
  );

  return { send, resume };
}
